import React from 'react';
import { motion } from 'framer-motion';
import { FileText, AlertTriangle, Scale, Shield, UserCheck, CreditCard, Ban, Clock, Globe, Settings, BookOpen, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../config/contactInfo';

const TermsOfService: React.FC = () => {
  const sections = [
    {
      icon: UserCheck,
      title: '1. Acceptance of Terms',
      content: [
        'By accessing or using any BioAxis service, you confirm that you have read, understood, and agree to be bound by these Terms, our Privacy Policy, Acceptable Use Policy, and all other policies referenced herein',
        'You must be at least 18 years of age and have the legal capacity to enter into a binding agreement under South African law',
        'If you are accepting these Terms on behalf of a company, organisation, or other entity, you represent that you have the authority to bind that entity',
        'If you do not agree to any part of these Terms, you must immediately cease using our Services',
        'These Terms are governed by the Electronic Communications and Transactions Act 25 of 2002 (ECT Act) and constitute an electronic agreement as contemplated therein'
      ]
    },
    {
      icon: BookOpen,
      title: '2. Description of Services',
      content: [
        'BioAxis provides cloud-based healthcare software solutions ("Services"), including but not limited to:',
        'VetAxis: Veterinary practice management software for animal healthcare professionals',
        'StaffAxis: Staff management and scheduling platform for healthcare facilities',
        'NoteAxis: Clinical documentation and note-taking solution for healthcare professionals',
        'Suite bundles combining multiple products at discounted rates',
        'Services are provided as Software-as-a-Service (SaaS) accessible via web browser, subject to subscription and payment terms',
        'BioAxis reserves the right to modify, update, or discontinue any Service with reasonable notice (minimum 30 days for material changes)'
      ]
    },
    {
      icon: Shield,
      title: '3. Account Registration & Responsibilities',
      content: [
        'You must register an account to access our Services, providing accurate, current, and complete information',
        'You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account',
        'You must immediately notify BioAxis of any unauthorised use of your account or any security breach',
        'Healthcare professionals are responsible for ensuring their use of our Services complies with HPCSA guidelines, the National Health Act, and applicable professional standards',
        'You must not share your account credentials with third parties or allow multiple users to access a single-user account',
        'BioAxis may suspend or terminate accounts that violate these Terms or pose a security risk'
      ]
    },
    {
      icon: CreditCard,
      title: '4. Subscription, Billing & Payment',
      content: [
        'Services are offered on monthly or annual subscription plans as advertised on our website, priced in South African Rand (ZAR)',
        'Payments are processed securely through PayFast (Pty) Ltd, a registered South African payment service provider',
        'Subscriptions automatically renew at the end of each billing period unless cancelled before the renewal date',
        'BioAxis may adjust pricing with at least 30 days\' written notice before the next billing cycle; continued use after the effective date constitutes acceptance',
        'All prices are inclusive of VAT at the prevailing rate as required by the Value-Added Tax Act 89 of 1991',
        'Failed payments may result in service suspension after a 7-day grace period with notification',
        'For detailed refund and cancellation terms, see our Refund & Cancellation Policy'
      ]
    },
    {
      icon: Scale,
      title: '5. Intellectual Property',
      content: [
        'All software, content, trademarks, logos, and materials comprising the BioAxis Services are owned by or licensed to BioAxis and are protected by South African intellectual property laws, including the Copyright Act 98 of 1978 and the Trade Marks Act 194 of 1993',
        'You are granted a limited, non-exclusive, non-transferable, revocable licence to access and use the Services for the duration of your subscription',
        'You retain full ownership of all data, including patient records, clinical notes, and other content you create or upload through the Services ("Your Data")',
        'You grant BioAxis a limited licence to process Your Data solely for the purpose of providing and maintaining the Services, in accordance with our Privacy Policy',
        'You may not reverse-engineer, decompile, copy, modify, or create derivative works of any BioAxis software or Services',
        'Feedback, suggestions, or ideas you voluntarily provide may be used by BioAxis to improve Services without obligation to you'
      ]
    },
    {
      icon: Ban,
      title: '6. Prohibited Conduct',
      content: [
        'You may not use the Services for any unlawful purpose or in violation of any applicable South African or international law',
        'You may not attempt to gain unauthorised access to any part of the Services, other accounts, or computer systems',
        'You may not transmit any malware, viruses, or harmful code through the Services',
        'You may not use the Services to store or transmit content that infringes third-party intellectual property rights',
        'You may not use automated tools (bots, scrapers, etc.) to access the Services without prior written consent',
        'You may not resell, sublicense, or commercially exploit the Services without written authorisation from BioAxis',
        'For full details, see our Acceptable Use Policy'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <FileText className="w-16 h-16 text-primary-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Terms of <span className="gradient-text">Service</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              These Terms of Service govern your use of BioAxis healthcare software solutions. They are governed by the laws of the Republic of South Africa.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 2026 &middot; Effective date: February 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* ECT Act Notice */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6">Agreement Overview</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("you", "your", or "User") and BioAxis (Pty) Ltd ("BioAxis", "we", "us", or "our"), a company registered in the Republic of South Africa, with its principal place of business in {contactInfo.business.address.full}.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms, our{' '}
              <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline">Privacy Policy</Link>,{' '}
              <Link to="/acceptable-use" className="text-primary-400 hover:text-primary-300 underline">Acceptable Use Policy</Link>, and{' '}
              <Link to="/refund-policy" className="text-primary-400 hover:text-primary-300 underline">Refund &amp; Cancellation Policy</Link>.
            </p>

            {/* ECT Act Section 43 Disclosure */}
            <div className="bg-primary-900/20 border border-primary-700/30 rounded-xl p-4 mt-6">
              <h3 className="text-lg font-semibold text-primary-300 mb-3 flex items-center gap-2">
                <Globe className="w-5 h-5" />
                ECT Act Section 43 Disclosure
              </h3>
              <div className="text-gray-300 text-sm space-y-1">
                <p><strong>Full name:</strong> BioAxis (Pty) Ltd</p>
                <p><strong>Physical address:</strong> {contactInfo.business.address.full}</p>
                <p><strong>Email:</strong> {contactInfo.email.primary}</p>
                <p><strong>Phone:</strong> {contactInfo.phone.display}</p>
                <p><strong>Website:</strong> www.bioaxis.co.za</p>
                <p><strong>Business hours:</strong> {contactInfo.business.hours.weekdays} ({contactInfo.business.hours.timezone})</p>
              </div>
            </div>
          </motion.div>

          {/* Terms Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="glass-effect glass-effect-hover p-8 rounded-2xl"
              >
                <div className="flex items-start gap-4 mb-6">
                  <section.icon className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
                  <h3 className="text-2xl font-bold">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-300">
                      <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Additional Important Sections */}
          <div className="space-y-8 mt-12">
            {/* Healthcare Disclaimer */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-effect p-8 rounded-2xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <AlertTriangle className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-1" />
                <h3 className="text-2xl font-bold">7. Healthcare Disclaimer & Limitation of Liability</h3>
              </div>
              <div className="text-gray-300 space-y-4">
                <p>
                  <strong>No medical advice:</strong> BioAxis provides healthcare software tools to assist healthcare professionals. Our Services do not constitute medical, veterinary, or clinical advice. We do not diagnose, treat, or prescribe.
                </p>
                <p>
                  <strong>Professional responsibility:</strong> Healthcare professionals remain solely responsible for all clinical decisions, patient care, and compliance with their professional regulatory bodies (including the HPCSA and SAVC). Our software is designed to support, not replace, clinical expertise and professional judgement.
                </p>
                <p>
                  <strong>Limitation of liability:</strong> To the maximum extent permitted by South African law, including the Consumer Protection Act 68 of 2008, BioAxis shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Services, including but not limited to loss of data, loss of revenue, or clinical outcomes.
                </p>
                <p>
                  <strong>Maximum liability:</strong> Our total aggregate liability for any claim arising from or related to the Services shall not exceed the total fees paid by you to BioAxis in the 12 months preceding the claim.
                </p>
                <p>
                  <strong>Consumer Protection Act:</strong> Nothing in these Terms is intended to limit or exclude any rights you may have under the Consumer Protection Act 68 of 2008 that cannot be lawfully excluded or limited.
                </p>
              </div>
            </motion.div>

            {/* Data Protection */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-effect p-8 rounded-2xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <Shield className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
                <h3 className="text-2xl font-bold">8. Data Protection & POPIA</h3>
              </div>
              <div className="text-gray-300 space-y-4">
                <p>
                  Both parties agree to comply with the Protection of Personal Information Act 4 of 2013 (POPIA) in relation to any personal information processed in connection with the Services.
                </p>
                <p>
                  Where you use the Services to process personal information of your patients, clients, or staff, you are the Responsible Party and BioAxis acts as an Operator. We will process such data only on your documented instructions and in accordance with our Privacy Policy and any data processing agreement between us.
                </p>
                <p>
                  You are responsible for obtaining all necessary consents from your data subjects and ensuring your use of the Services complies with POPIA. For full details, see our{' '}
                  <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline">Privacy Policy</Link> and{' '}
                  <Link to="/popia" className="text-primary-400 hover:text-primary-300 underline">POPIA Compliance</Link> page.
                </p>
              </div>
            </motion.div>

            {/* Service Availability & Termination */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="glass-effect p-6 rounded-xl"
              >
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Settings className="w-6 h-6 text-primary-400" />
                  9. Service Availability
                </h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>&#8226; We aim for 99.9% uptime but do not guarantee uninterrupted service</li>
                  <li>&#8226; Scheduled maintenance will be communicated at least 48 hours in advance where possible</li>
                  <li>&#8226; Emergency maintenance may be performed without prior notice to address security or stability issues</li>
                  <li>&#8226; BioAxis shall not be liable for downtime caused by force majeure events, including load shedding, internet outages, or third-party service failures</li>
                  <li>&#8226; Service Level Agreements may be available for enterprise clients upon request</li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="glass-effect p-6 rounded-xl"
              >
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <RefreshCw className="w-6 h-6 text-primary-400" />
                  10. Termination & Suspension
                </h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>&#8226; You may cancel your subscription at any time through your account settings or by contacting support</li>
                  <li>&#8226; BioAxis may suspend or terminate your account for breach of these Terms with 14 days&apos; written notice (or immediately for serious breaches)</li>
                  <li>&#8226; Upon termination, you may request export of Your Data within 30 days</li>
                  <li>&#8226; After the 30-day data retrieval period, Your Data will be securely deleted in accordance with our data retention policies</li>
                  <li>&#8226; Sections relating to intellectual property, limitation of liability, and governing law survive termination</li>
                </ul>
              </motion.div>
            </div>

            {/* CPA Cooling-Off */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-effect p-8 rounded-2xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <Clock className="w-8 h-8 text-accent-400 flex-shrink-0 mt-1" />
                <h3 className="text-2xl font-bold">11. Consumer Protection Act Rights</h3>
              </div>
              <div className="text-gray-300 space-y-4">
                <p>
                  <strong>Cooling-off period (CPA Section 44):</strong> If you subscribed to our Services via electronic transaction (online), you have the right to cancel within 5 business days of the transaction without reason or penalty. Refunds will be processed within 15 business days of cancellation.
                </p>
                <p>
                  <strong>Fixed-term agreements (CPA Section 14):</strong> Annual subscriptions are treated as fixed-term agreements. You may cancel at any time by giving 20 business days&apos; written notice. Early cancellation may be subject to a reasonable cancellation penalty as permitted by the CPA.
                </p>
                <p>
                  <strong>Right to fair and honest dealing (CPA Section 40-41):</strong> BioAxis commits to transparent pricing, honest representation of our Services, and fair contractual terms.
                </p>
                <p>
                  For full cancellation and refund details, see our{' '}
                  <Link to="/refund-policy" className="text-primary-400 hover:text-primary-300 underline">Refund &amp; Cancellation Policy</Link>.
                </p>
              </div>
            </motion.div>

            {/* Governing Law */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-effect p-8 rounded-2xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <Scale className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
                <h3 className="text-2xl font-bold">12. Governing Law & Dispute Resolution</h3>
              </div>
              <div className="text-gray-300 space-y-4">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the Republic of South Africa, including the Consumer Protection Act 68 of 2008, the Electronic Communications and Transactions Act 25 of 2002, and the Protection of Personal Information Act 4 of 2013.
                </p>
                <p>
                  <strong>Informal resolution:</strong> The parties shall first attempt to resolve any dispute through good-faith negotiation. Either party may initiate this process by sending written notice of the dispute to the other party.
                </p>
                <p>
                  <strong>Mediation:</strong> If the dispute is not resolved within 30 days of the written notice, either party may refer the matter to mediation in accordance with the rules of the Arbitration Foundation of Southern Africa (AFSA).
                </p>
                <p>
                  <strong>Arbitration:</strong> If mediation fails, the dispute shall be finally resolved by arbitration administered by AFSA in accordance with its rules. The arbitration shall take place in Pretoria, Gauteng, South Africa.
                </p>
                <p>
                  <strong>Consumer complaints:</strong> Nothing in this clause prevents you from approaching the National Consumer Commission, the relevant Ombud, or any court of competent jurisdiction as provided for under the Consumer Protection Act.
                </p>
                <p>
                  <strong>Jurisdiction:</strong> The High Court of South Africa, Gauteng Division, Pretoria, shall have jurisdiction over any matter not subject to arbitration.
                </p>
              </div>
            </motion.div>

            {/* General Provisions */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-effect p-8 rounded-2xl"
            >
              <h3 className="text-2xl font-bold mb-6">13. General Provisions</h3>
              <div className="text-gray-300 space-y-4">
                <p>
                  <strong>Entire agreement:</strong> These Terms, together with our Privacy Policy, Acceptable Use Policy, and Refund &amp; Cancellation Policy, constitute the entire agreement between you and BioAxis regarding the use of our Services.
                </p>
                <p>
                  <strong>Severability:</strong> If any provision of these Terms is found to be unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect.
                </p>
                <p>
                  <strong>Waiver:</strong> The failure of BioAxis to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
                </p>
                <p>
                  <strong>Assignment:</strong> You may not assign or transfer these Terms without prior written consent from BioAxis. BioAxis may assign these Terms in connection with a merger, acquisition, or sale of assets.
                </p>
                <p>
                  <strong>Amendments:</strong> BioAxis reserves the right to amend these Terms at any time. Material changes will be notified at least 30 days in advance via email or prominent website notice. Continued use after the effective date constitutes acceptance.
                </p>
                <p>
                  <strong>Language:</strong> These Terms are drafted in English. In the event of any conflict between an English version and a translation, the English version shall prevail.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12 text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Questions About These Terms?</h3>
            <p className="text-gray-300 mb-6">
              If you have any questions about these Terms of Service, please contact our legal team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={`mailto:${contactInfo.email.legal}`}
                whileHover={{ scale: 1.05 }}
                className="btn-primary"
              >
                Email Legal Team
              </motion.a>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                className="btn-outline"
              >
                Contact Us
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
